import { Button } from '@/components/Button';
import Input from '@/components/Input';
import Select from '@/components/Select';
import { MaterialIcon } from '@/components/MaterialIcon';
import type { TagCondition } from '../types/TagCondition';
import {
  FIELD_OPTIONS,
  operatorOptionsFor,
  SALARY_RANGE_COLUMN,
  SALARY_RANGE_TYPE,
  parseSalaryRange,
  buildSalaryRangeContent,
  isSalaryRangeCondition,
  COMPANY_ID_COLUMN,
  isCompanyIdCondition,
} from '../types/TagCondition';
import CompanyMultiSelectCondition from './CompanyMultiSelectCondition';
import { useState } from 'react';

interface Props {
  condition: TagCondition;
  onUpdate: (condition: TagCondition) => void;
  onDelete: () => void;
}

/**
 * タグ付け条件の1行を表示するコンポーネント
 */
export default function TagConditionRow({ condition, onUpdate, onDelete }: Props) {
  const [isFocused, setIsFocused] = useState(false);

  // 年収レンジ・企業選択との相互切替時に condition_type / condition_content をリセットする
  const handleFieldChange = (value: string) => {
    if (value === SALARY_RANGE_COLUMN) {
      // 年収レンジへ切り替え: type と content を年収レンジ用に設定
      onUpdate({
        ...condition,
        condition_column: SALARY_RANGE_COLUMN,
        condition_type: SALARY_RANGE_TYPE,
        condition_content: ',',
      });
    } else if (value === COMPANY_ID_COLUMN) {
      // 企業選択へ切り替え: type は in 固定、content は未選択状態にリセット
      onUpdate({
        ...condition,
        condition_column: COMPANY_ID_COLUMN,
        condition_type: 'in',
        condition_content: '',
      });
    } else {
      // 年収レンジ・企業選択以外へ切り替え: type と content を通常用にリセット
      onUpdate({
        ...condition,
        condition_column: value,
        condition_type: 'eq',
        condition_content: '',
      });
    }
  };

  // 年収レンジ条件の場合は専用UIを描画
  if (isSalaryRangeCondition(condition)) {
    const { min, max } = parseSalaryRange(condition.condition_content);
    return (
      <div className="flex items-end gap-3">
        {/* 項目選択（年収） */}
        <div className="w-[144px]">
          <Select
            items={FIELD_OPTIONS}
            value={condition.condition_column}
            onValueChange={handleFieldChange}
            className="w-full"
          />
        </div>

        {/* 下限入力 */}
        <div className="w-[120px]">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={min}
            onChange={(e) =>
              onUpdate({
                ...condition,
                condition_content: buildSalaryRangeContent(e.target.value, max),
              })
            }
            placeholder="下限"
            className="w-full"
          />
        </div>

        {/* 万円テキスト */}
        <span className="mb-3 text-sm text-black">万円</span>

        {/* 範囲区切り */}
        <span className="mb-3 text-sm text-black">〜</span>

        {/* 上限入力 */}
        <div className="w-[120px]">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={max}
            onChange={(e) =>
              onUpdate({
                ...condition,
                condition_content: buildSalaryRangeContent(min, e.target.value),
              })
            }
            placeholder="上限"
            className="w-full"
          />
        </div>

        {/* 万円テキスト */}
        <span className="mb-3 text-sm text-black">万円</span>

        {/* 削除ボタン */}
        <Button
          onClick={onDelete}
          color="danger-secondary"
          size="md"
          className="size-[42px] gap-0 p-0"
        >
          <MaterialIcon name="cancel" className="text-xl" />
        </Button>
      </div>
    );
  }

  // 企業選択条件の場合は専用UIを描画
  if (isCompanyIdCondition(condition)) {
    return (
      <div className="flex items-start gap-3">
        {/* 項目選択（企業） */}
        <div className="w-[144px]">
          <Select
            items={FIELD_OPTIONS}
            value={condition.condition_column}
            onValueChange={handleFieldChange}
            className="w-full"
          />
        </div>

        {/* 企業の複数選択UI */}
        <div className="flex-1">
          <CompanyMultiSelectCondition
            value={condition.condition_content}
            onChange={(content) => onUpdate({ ...condition, condition_content: content })}
          />
        </div>

        {/* 演算子選択（in 固定） */}
        <div className="w-[276px]">
          <Select
            items={operatorOptionsFor(condition.condition_column)}
            value={condition.condition_type}
            onValueChange={() => {}}
            className="w-full"
            disabled
          />
        </div>

        {/* 削除ボタン */}
        <Button
          onClick={onDelete}
          color="danger-secondary"
          size="md"
          className="size-[42px] shrink-0 gap-0 p-0"
        >
          <MaterialIcon name="cancel" className="text-xl" />
        </Button>
      </div>
    );
  }

  // 通常条件の場合（キーワード入力 + 演算子選択）
  return (
    <div className="flex items-end gap-3">
      {/* 項目選択 */}
      <div className="w-[144px]">
        <Select
          items={FIELD_OPTIONS}
          value={condition.condition_column}
          onValueChange={handleFieldChange}
          className="w-full"
        />
      </div>

      {/* 「に」テキスト */}
      <span className="mb-3 text-sm text-black">に</span>

      {/* キーワード入力 */}
      <div className="relative flex-1">
        <Input
          value={condition.condition_content}
          onChange={(e) => onUpdate({ ...condition, condition_content: e.target.value })}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="キーワード"
          className="w-full"
        />
        {isFocused && (
          <div className="absolute top-full left-0 z-10 mt-1 rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-md">
            カンマ区切りで複数設定できます
            <div className="absolute top-0 left-4 size-0 -translate-y-1/2 border-x-4 border-b-4 border-x-transparent border-b-gray-900" />
          </div>
        )}
      </div>

      {/* 演算子選択 */}
      <div className="w-[276px]">
        <Select
          items={operatorOptionsFor(condition.condition_column)}
          value={condition.condition_type}
          onValueChange={(value) => {
            onUpdate({ ...condition, condition_type: value });
          }}
          className="w-full"
        />
      </div>

      {/* 削除ボタン */}
      <Button
        onClick={onDelete}
        color="danger-secondary"
        size="md"
        className="size-[42px] gap-0 p-0"
      >
        <MaterialIcon name="cancel" className="text-xl" />
      </Button>
    </div>
  );
}
